<!--
  SupplierBasePage — the tabbed shell host for the Suppliers-Club app.

  This is the supplier-side mirror of BasePage.vue. It exists as a SEPARATE,
  physically-static shell (rather than a parameterized one) because Framework7
  needs each routable-tabs shell to be a real, statically-defined route tree —
  see the club-parameterized-shell architecture doc Section 4.2/Section 5 and
  _bmad/data/f7v9-vue3-playbook.md Section 5.

  Why this is a thin wrapper around <supplier-tabbar-navigation> with a
  CONTENT-LESS <f7-page> (`:page-content="false"`, playbook Section 5 rows 17 + 25):

  In routes.js the parent route `/suppliers/` has `tabs: [...]` plus an eager
  `component: SupplierBasePage`. Per Framework7 docs, the parent's component
  must render the F7 tab CONTAINERS (`<f7-tabs routable>` with `<f7-tab id>`
  children) — NOT a full `<f7-page>` (with its own `.page-content`). Each tab's
  loaded component (SupplierWallPostsPage = the merged Opportunities window,
  SupplierFeedPage, ProfilePage) provides its OWN `<f7-page>` + `<f7-navbar>`,
  which F7 mounts INSIDE the matching `<f7-tab>`.

  Wrapping this in a NORMAL `<f7-page>` (the default `page-content: true`)
  would create a NESTED `.page-content` layout (outer page-content =
  SupplierBasePage, inner page-content = the tab) — F7's CSS for routable tabs
  doesn't account for that: the outer tabbar collapses to 0×0 and the inner
  active tab clips to ~50px, hiding all body content (playbook Section 5 row 17,
  verified empirically on the seller shell).

  `<f7-page :page-content="false">` avoids that: per
  `framework7-vue/components/page.js`, `page-content: false` takes the OTHER
  render branch — NO `f7PageContent` wrapper, direct children only (toolbar +
  routable tabs), so there is still only ONE real `.page-content` in the whole
  tree (the loaded tab's own). It DOES add the base `.page` class F7 core needs
  — `.page > .tabs { height: 100% }` (framework7-bundle.css) is F7's own CSS for
  exactly this "routable-tabs parent page" shape, so `<f7-tabs>` still fills the
  full height and the toolbar stays pinned to the bottom, unchanged visually
  from the row-17-era plain-div version.

  Row 25 (`master:true` + a pushed `hideTabbar` detail route + back-navigation):
  the PLAIN-DIV version (no `.page` class at all) broke `router.back()` — F7
  core's `back.js` / `navigate.js` locate the previous/current page purely via
  `.page`/`.page-current`/`.page-previous` CLASS selectors on `view.$el`'s
  direct children. Without the base `.page` class, this shell was invisible to
  that bookkeeping: `navigate.js`'s forward-nav old-page detection never
  reclassed it `page-previous` when the detail route was pushed, so
  `back.js`'s `.page-current → .prevAll('.page-previous')` lookup came up empty
  on the way back, and `back()` fell through to re-resolving `/suppliers/orders/`
  from scratch — re-mounting SupplierBasePage as a SECOND, duplicate shell (see
  playbook Section 5 row 25 for the full trace). The `.page` class fixes this: F7 now
  correctly reclasses the shell `page-previous` on the way out and finds/reuses
  it on the way back — verified via a throwaway Playwright DOM-count harness
  (2+ duplicate shells/tabbars → exactly 1) before landing this fix.

  The tab ids are GLOBALLY UNIQUE (`suppliers-wall` / `suppliers-feed` /
  `suppliers-profile`, never `wall`/`home`) so they cannot collide with the
  seller shell's `<f7-tab id>` stubs during the cross-shell swap-transition DOM
  window — F7's tab.js resolves `#id` by live DOM query (architecture doc Section 5.1).

  The tabbar visibility (`v-show="showSupplierTabbar"`) is owned by
  SupplierTabbarNavigation, which injects `showSupplierTabbar` from AppLayout.
-->
<template>
  <f7-page :page-content="false" name="suppliers-shell">
    <supplier-tabbar-navigation></supplier-tabbar-navigation>
  </f7-page>
</template>

<script setup>
import SupplierTabbarNavigation from '@/components/SupplierTabbarNavigation.vue';

defineOptions({
  name: 'SupplierBasePage',
});
</script>
